import { useEffect, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { api, apiMessage } from '@/lib/api';
import { toast } from '@/hooks/use-toast';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tag the lead with where the click came from (e.g. 'hero', 'footer'). */
  source?: string;
}

interface Form {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

const blank: Form = { name: '', email: '', phone: '', company: '', message: '' };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function RequestDemoDialog({ open, onOpenChange, source }: Props) {
  const [form, setForm] = useState<Form>(blank);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof Form, string>>>({});

  useEffect(() => {
    if (open) {
      setForm(blank);
      setSubmitted(false);
      setErrors({});
    }
  }, [open]);

  const submit = useMutation({
    mutationFn: async () => {
      await api.post('/v1/demo-requests', {
        fullName: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        message: form.message.trim() || undefined,
        source: source || 'landing',
      });
    },
    onSuccess: () => setSubmitted(true),
    onError: (err) =>
      toast({
        title: "Couldn't send your request",
        description: apiMessage(err, 'Please try again in a moment.'),
        variant: 'destructive',
      }),
  });

  function validate(): boolean {
    const next: Partial<Record<keyof Form, string>> = {};
    if (!form.name.trim()) next.name = 'Required';
    if (!form.email.trim()) next.email = 'Required';
    else if (!EMAIL_RE.test(form.email.trim())) next.email = 'Enter a valid email';
    if (!form.phone.trim()) next.phone = 'Required';
    if (!form.company.trim()) next.company = 'Required';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    submit.mutate();
  }

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    setForm((p) => ({ ...p, [key]: value }));
    if (errors[key]) setErrors((p) => ({ ...p, [key]: undefined }));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {submitted ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <div className="inline-flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <CheckCircle2 className="size-6" aria-hidden />
            </div>
            <DialogTitle className="text-lg">Thanks — we'll be in touch</DialogTitle>
            <DialogDescription>
              We received your request and will reach out within one business day.
            </DialogDescription>
            <Button className="mt-2" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            <DialogHeader>
              <DialogTitle>Request a demo</DialogTitle>
              <DialogDescription>
                Tell us how to reach you — we'll set up a walkthrough tailored
                to your stack.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-5 grid gap-4">
              <Field
                id="demo-name"
                label="Full name"
                value={form.name}
                onChange={(v) => update('name', v)}
                error={errors.name}
                autoComplete="name"
                placeholder="Jane Doe"
                required
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  id="demo-email"
                  label="Work email"
                  type="email"
                  value={form.email}
                  onChange={(v) => update('email', v)}
                  error={errors.email}
                  autoComplete="email"
                  required
                />
                <Field
                  id="demo-phone"
                  label="Phone"
                  type="tel"
                  value={form.phone}
                  onChange={(v) => update('phone', v)}
                  error={errors.phone}
                  autoComplete="tel"
                  placeholder="+1 555 123 4567"
                  required
                />
              </div>
              <Field
                id="demo-company"
                label="Company, app name, or website"
                value={form.company}
                onChange={(v) => update('company', v)}
                error={errors.company}
                autoComplete="organization"
                placeholder="Acme Inc · acme.app · acme.com"
                required
              />
              <div className="grid gap-1.5">
                <Label htmlFor="demo-message">
                  Anything you'd like us to know{' '}
                  <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Textarea
                  id="demo-message"
                  rows={3}
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                  placeholder="Platforms, expected volume, timeline…"
                />
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submit.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submit.isPending}>
                {submit.isPending ? (
                  <>
                    <Loader2 className="animate-spin" aria-hidden />
                    Sending…
                  </>
                ) : (
                  'Send request'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
  error?: string;
}

function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  required,
  placeholder,
  autoComplete,
  error,
}: FieldProps) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {error && (
        <p id={`${id}-error`} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
